import {
  Toolbar as KendoToolbar,
  ToolbarItem,
  ToolbarSpacer,
  ToolbarSeparator,
  Button,
  Chip,
} from '@progress/kendo-react-buttons';
import type { ChipProps } from '@progress/kendo-react-buttons';
import { Loader } from '@progress/kendo-react-indicators';
import { useFormBuilder } from '../context/FormBuilderContext';
import type { FormVersionStatus } from '../types';

const STATUS_THEME: Record<FormVersionStatus, ChipProps['themeColor']> = {
  draft: 'warning',
  published: 'success',
  archived: 'base',
};

export function Toolbar({ onToggleVersions }: { onToggleVersions: () => void }) {
  const {
    formDefinition,
    activeVersion,
    isDirty,
    isEditable,
    isImporting,
    importFromScreenshot,
    saveDraft,
    publish,
    createVersionFromCurrent,
    togglePreview,
    previewOpen,
    onNavigateBack,
  } = useFormBuilder();

  return (
    <KendoToolbar>
      {onNavigateBack && (
        <ToolbarItem>
          <Button fillMode="flat" onClick={onNavigateBack} title="Back to Form Library">
            ← Back
          </Button>
        </ToolbarItem>
      )}
      <ToolbarItem>
        <span style={{ fontWeight: 'var(--kendo-font-weight-bold)', fontSize: 'var(--kendo-font-size-md)' }}>
          {formDefinition.name}
        </span>
      </ToolbarItem>
      <ToolbarItem>
        <Chip text={`v${activeVersion.versionNumber} · ${activeVersion.status}`} themeColor={STATUS_THEME[activeVersion.status]} />
      </ToolbarItem>
      {isDirty && (
        <ToolbarItem>
          <Chip text="Unsaved changes" themeColor="warning" fillMode="outline" />
        </ToolbarItem>
      )}

      <ToolbarSpacer />

      {importFromScreenshot && isEditable && (
        <ToolbarItem>
          <Button
            fillMode="outline"
            disabled={isImporting}
            onClick={importFromScreenshot}
            title="Generate form layout from a screenshot"
          >
            {isImporting ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Loader size="small" type="pulsing" /> Importing…
              </span>
            ) : (
              '✦ Import from Screenshot'
            )}
          </Button>
        </ToolbarItem>
      )}

      <ToolbarItem>
        <Button fillMode="outline" onClick={onToggleVersions}>
          Versions
        </Button>
      </ToolbarItem>
      <ToolbarItem>
        <Button fillMode={previewOpen ? 'solid' : 'outline'} onClick={togglePreview}>
          Preview
        </Button>
      </ToolbarItem>

      <ToolbarSeparator />

      {isEditable ? (
        <>
          <ToolbarItem>
            <Button themeColor="base" disabled={!isDirty} onClick={saveDraft}>
              Save Draft
            </Button>
          </ToolbarItem>
          <ToolbarItem>
            <Button themeColor="primary" onClick={publish}>
              Publish
            </Button>
          </ToolbarItem>
        </>
      ) : (
        <ToolbarItem>
          <Button themeColor="primary" onClick={createVersionFromCurrent}>
            Create New Version to Edit
          </Button>
        </ToolbarItem>
      )}
    </KendoToolbar>
  );
}
